import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import MonthSelector from './components/MonthSelector'
import UnsettledMonthsList from './components/UnsettledMonthsList'
import RealizationTable from './components/RealizationTable'
import ManualEntriesTable from './components/ManualEntriesTable'
import useRozliczeniaDetails from './hooks/useRozliczeniaDetails'

const formatMoney = value => {
	const number = Number(value)
	if (!Number.isFinite(number)) return '0'
	return number.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

export default function RozliczeniaDetails() {
	const { t } = useTranslation()
	const {
		trainer,
		loading,
		saving,
		year,
		month,
		unsettledMonths,
		realizationRows,
		entries,
		przelewy,
		companies,
		rozliczono,
		setRozliczono,
		totals,
		addEntry,
		updateEntry,
		removeEntry,
		addPrzelew,
		updatePrzelew,
		removePrzelew,
		save,
		goPrevMonth,
		goNextMonth,
		selectMonth,
		setYear,
		setMonth,
	} = useRozliczeniaDetails()

	if (loading && !trainer) {
		return <LoadingIndicator message={t('rozliczenia.details.loading')} />
	}

	if (!trainer) {
		return (
			<div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
				<p className="text-gray-700">{t('rozliczenia.details.notFound')}</p>
				<Link
					to="/ksiegowosc/rozliczenia"
					className="mt-4 inline-flex text-sm font-medium text-blue-600 hover:text-blue-800"
				>
					{t('rozliczenia.details.backToList')}
				</Link>
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<Link
						to="/ksiegowosc/rozliczenia"
						className="text-sm font-medium text-blue-600 hover:text-blue-800"
					>
						{t('rozliczenia.details.backToList')}
					</Link>
					<h1 className="mt-2 text-2xl font-semibold text-gray-900">
						{t('rozliczenia.details.title', { name: trainer.nazwa || '—' })}
					</h1>
					<p className="mt-1 text-sm text-gray-500">
						{[trainer.miasto, trainer.wojewodztwo, trainer.email].filter(Boolean).join(' · ') || '—'}
					</p>
				</div>

				<button
					type="button"
					onClick={save}
					disabled={saving}
					className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{saving ? t('rozliczenia.details.saving') : t('rozliczenia.details.save')}
				</button>
			</div>

			<UnsettledMonthsList
				months={unsettledMonths}
				year={year}
				month={month}
				onSelect={selectMonth}
			/>

			<MonthSelector
				year={year}
				month={month}
				onPrev={goPrevMonth}
				onNext={goNextMonth}
				onYearChange={setYear}
				onMonthChange={setMonth}
			/>

			<RealizationTable rows={realizationRows} totals={totals} />

			<ManualEntriesTable
				entries={entries}
				entriesTotal={totals?.entries || 0}
				onAdd={addEntry}
				onChange={updateEntry}
				onRemove={removeEntry}
				disabled={saving}
				translationKey="rozliczenia.details.entries"
				totalPrefix="+"
			/>

			<ManualEntriesTable
				entries={przelewy}
				entriesTotal={totals?.przelewy || 0}
				onAdd={addPrzelew}
				onChange={updatePrzelew}
				onRemove={removePrzelew}
				disabled={saving}
				translationKey="rozliczenia.details.transfers"
				totalPrefix="−"
				showCompany
				companies={companies}
			/>

			<div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-4 shadow-sm">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<span className="text-base font-semibold text-indigo-950">
						{t('rozliczenia.details.toSettleLabel')}
					</span>
					<span className="text-xl font-bold text-indigo-950">
						{formatMoney(totals?.do_rozliczenia)} PLN
					</span>
				</div>
				<p className="mt-1 text-sm text-indigo-800">
					{t('rozliczenia.details.toSettleHelp')}
				</p>
			</div>

			<div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
				<label className="inline-flex items-center gap-3 text-base font-medium text-gray-900">
					<input
						type="checkbox"
						checked={rozliczono}
						onChange={event => setRozliczono(event.target.checked)}
						disabled={saving}
						className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
					/>
					{t('rozliczenia.details.settled')}
				</label>
				<p className="mt-2 text-sm text-gray-500">
					{t('rozliczenia.details.settledHelp')}
				</p>
			</div>

			<div className="flex justify-end">
				<button
					type="button"
					onClick={save}
					disabled={saving}
					className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{saving ? t('rozliczenia.details.saving') : t('rozliczenia.details.save')}
				</button>
			</div>
		</div>
	)
}
